// What AO publishes to the One Retail shell once embedded: its navbar items, the shell home page's
// recent/frequent entries, the browser URL and the assistant provider. It also follows the shell's
// language selection, the one thing here that flows the other way.
//
// Install AFTER loadUserContext has resolved and the app is mounted: the nav labels are translated
// against catalogues the user context supplies, and setLanguageLocale resolves against the same list.

import { watch } from 'vue';
import { get } from 'lodash';
import i18n from '@/plugins/vue-i18n';
import { useContextStore } from '@/stores/context';
import { installAiRouteContextReset } from '@/composables/use-ai-side-panel';
import { watchAndReportCurrentRoute } from '@/webshell-embed/recently-visited';
import { reportTabTitleDetail, watchAndReportTabTitle } from '@/webshell-embed/tab-title';
import { getTabTitleDetailFromRoute } from '@/utils/tab-title-from-route';
import { APP_KEY, PLATFORM_BRIDGE_VERSION, shellBridge } from '@/webshell-embed/platform-bridge';
import {
  registerShellAssistantProvider,
  unregisterShellAssistantProvider,
} from '@/webshell-embed/shell-assistant-provider';
import { subscribeToShellLocale, unsubscribeFromShellLocale } from '@/webshell-embed/shell-locale';

// Shell navbar items derived from AO's route table. Visibility and order mirror MainNavbar.vue's
// `routes` computed, so the contributed nav matches AO's own navbar.
function buildMainNavbarItems(router, contextStore) {
  const permitted = route => {
    if (route.omitFromNav || !route.linkText) return false;
    const perm = get(route.meta, 'requiresPermission');
    return perm ? contextStore.hasPermission(perm) : true;
  };
  const featureVisible = route => {
    switch (route.name) {
      case 'AdminPage':
        return contextStore.getShowAdminControlsPermissions;
      case 'ClientAdminPage':
        return contextStore.getShowClientAdminControlsPermissions;
      case 'ReportingPage':
        return contextStore.getShowReportingControlsPermissions;
      case 'ReportingPowerBIPage':
        return contextStore.getShowReportingPowerBIPermissions;
      case 'CompoundExtractsPage':
        return contextStore.getShowCompoundExtractsPermissions;
      case 'TemplatesPage':
        return contextStore.getShowTemplatesPermissions;
      case 'BundlesPage':
        return contextStore.getShowBundlesPermissions;
      default:
        return true;
    }
  };
  const visible = route => permitted(route) && featureVisible(route);
  // By `index`, no-index routes last, then by name. The `order` assigned below is sequential
  // because the shell re-sorts by it, and raw `route.index` would float no-index routes to the top.
  const byNavbarOrder = (a, b) => {
    if (Number.isFinite(a.index) && Number.isFinite(b.index)) return a.index - b.index;
    if (Number.isFinite(a.index)) return -1;
    if (Number.isFinite(b.index)) return 1;
    // Same tiebreak as MainNavbar.vue, so the two navs agree.
    return (b.name || '').localeCompare(a.name || '');
  };
  return router.options.routes
    .filter(visible)
    .sort(byNavbarOrder)
    .map((route, i) => ({
      id: route.id || route.name || route.path,
      label: i18n.t(route.linkText),
      to: route.path,
      order: i,
      children: (route.children || [])
        .filter(child => child.linkText && !route.omitChildrenFromNavbar)
        .map(child => ({
          id: child.id || child.name || child.path,
          label: i18n.t(child.linkText),
          to: child.path,
        })),
    }));
}

// Contribute AO's nav to the shell navbar as declarative data - ADR-007 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/007-navigation-model-and-contribution.md
// re-published whenever the user context changes. Gated on an authenticated profile: the login page
// has no context and no translations, so it contributes nothing rather than an untranslated
// snapshot.
const installNavbarItems = (router, contextStore) => {
  const syncNavbarItems = () => {
    const bridge = shellBridge();
    if (!bridge) return;
    if (!contextStore.currentUserProfile) {
      bridge.navbar.unregister(APP_KEY);
      return;
    }
    bridge.navbar.register(APP_KEY, buildMainNavbarItems(router, contextStore));
  };

  return watch(
    () => [
      contextStore.currentUserProfile,
      contextStore.getShowAdminControlsPermissions,
      contextStore.getShowClientAdminControlsPermissions,
      contextStore.getShowReportingControlsPermissions,
      contextStore.getShowReportingPowerBIPermissions,
      contextStore.getShowCompoundExtractsPermissions,
      contextStore.getShowTemplatesPermissions,
      contextStore.getShowBundlesPermissions,
    ],
    syncNavbarItems,
    { immediate: true, deep: true }
  );
};

// Let the shell navigate using AO's router, and report back to shell the current AO route.
// AO navigates on a memory history, so every route change has to be reported for the shell to write
// it into the URL and highlight the matching navbar item.
// The report during install replaces: the shell already pushed the entry that opened AO.
const installAppRouter = router => {
  shellBridge()?.appRouter.register(APP_KEY, to => router.push(to));

  const reportCurrentRoute = replace =>
    shellBridge()?.appRouter.reportCurrentRoute(APP_KEY, router.currentRoute.value.fullPath, {
      replace,
    });
  router.afterEach(() => reportCurrentRoute(false));
  reportCurrentRoute(true);
};

// Feed the shell home page's recent/frequent lists from the route's `recordRecentlyOpenedInShell`
// metadata.
const installRecents = router =>
  watchAndReportCurrentRoute(
    () => router.currentRoute.value,
    entry => shellBridge()?.recents.record(APP_KEY, entry)
  );

// The shell writes the browser tab; AO reports the page it is showing - ADR-018 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/018-shell-owns-the-tab-title.md
const installTabTitle = router =>
  watchAndReportTabTitle(
    () => router.currentRoute.value,
    route => getTabTitleDetailFromRoute(route, { waitForScenario: true })
  );

// The shell owns the assistant panel and conversation; AO contributes a provider it calls per turn
// against AO's own /completions endpoint - ADR-012 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/012-ai-assistant-extraction.md
// The route reset comes from AiSidePanel.vue standalone, and that is not rendered embedded, so
// install it here: without it the provider keeps handing the shell the context of a page the user
// has already left.
const installAssistant = router => {
  installAiRouteContextReset(router);
  registerShellAssistantProvider();
};

// Stop handles for the watchers that outlive the app: they observe stores in the shell's pinia, not
// AO's, so `app.unmount()` does not take them with it. The router's `afterEach` hooks need none —
// that router is created per app and discarded with it.
let stopWatchers = [];

// Call once when embedded, after mount and after the landing navigation.
export function installShellContributions(router) {
  const bridge = shellBridge();
  if (!bridge) return;
  // Lets the shell warn about, and hold back on, a product ahead of it - ADR-019 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/019-bridge-versioning.md
  bridge.reportBridgeVersion(APP_KEY, PLATFORM_BRIDGE_VERSION);

  const contextStore = useContextStore();
  stopWatchers = [
    installNavbarItems(router, contextStore),
    installRecents(router),
    installTabTitle(router),
  ];
  installAppRouter(router);
  installAssistant(router);
  subscribeToShellLocale();
}

// Withdraw everything above. The shell's stores outlive AO's app instance, so anything left behind
// follows the user into the next product.
export function uninstallShellContributions() {
  stopWatchers.forEach(stop => stop());
  stopWatchers = [];
  shellBridge()?.navbar.unregister(APP_KEY);
  shellBridge()?.appRouter.unregister(APP_KEY);
  reportTabTitleDetail(null);
  unregisterShellAssistantProvider();
  unsubscribeFromShellLocale();
}
